import MicroModal from 'micromodal';
/**
 * CustomMicroModal enhances MicroModal interactions by automatically initializing modals with custom
 * configurations for accessibility and UX. It handles modal show/close events, adds a body class when
 * modals are open, and intercepts clicks on specific anchor tags to show modals instead of navigating.
 *
 * Key Features:
 * - Custom event dispatching on modal show/close.
 * - Conditional modal opening based on anchor href attributes.
 * - Integration with MicroModal library for modal management.
 *
 * Designed for single instantiation per page, it requires MicroModal to be included in the project.
 *
 * Author: Dreamers of Day
 */
class CustomMicroModal {
    constructor() {
        this.initModal();
        this.registerModalOpener();
        this.registerModalCloser();
        this.registerAutoPopup();
    }

    /**
     * Initializes MicroModal with custom show and close behaviors,
     * including adding a custom class to the body and dispatching custom events.
     */
    initModal() {
        MicroModal.init({
            onShow: (modal) => {
                // Adds a custom class to the body when the modal is shown
                document.body.classList.add('l-body--modal-open');
                // Dispatches a custom 'MicroModal.show' event
                document.dispatchEvent(
                    new CustomEvent('MicroModal.show', {
                        detail: { modal },
                    }),
                );
            },
            onClose: (modal) => {
                // Removes the custom class from the body when the modal is closed
                document.body.classList.remove('l-body--modal-open');
                // Dispatches a custom 'MicroModal.close' event
                document.dispatchEvent(
                    new CustomEvent('MicroModal.close', {
                        detail: { modal },
                    }),
                );
            },
            disableScroll: true,
            awaitOpenAnimation: false,
            awaitCloseAnimation: true,
        });
    }

    /**
     * Registers a click event listener to open modals based on link href attributes.
     * Only processes links with hrefs starting with '#modal-'.
     */
    registerModalOpener() {
        document.addEventListener('click', (e) => {
            const target = e.target.closest('a');
            if (!target || !this.shouldOpenModal(target)) {
                return false;
            }
            e.preventDefault();

            const href = target.getAttribute('href');
            MicroModal.show(href.replace('#', ''), { disableScroll: true });
        });
    }

    /**
     * Registers an event listener to close the modal when button with the `data-micromodal-close` attribute
     * are clicked. This includes ensuring that clicks on nested children of such elements are also handled correctly.
     */
    registerModalCloser() {
        document.addEventListener('click', (e) => {
            // Identify the closest button with the `data-micromodal-close` attribute
            const closeTrigger = e.target.closest('.a-btn[data-micromodal-close]');
            
            // Ensure the button exists and proceed
            if (closeTrigger) {
                e.preventDefault(); // Prevent default action
                MicroModal.close(); // Trigger the modal close logic
            }
        });
    }

    /**
     * Determines if the clicked link should trigger opening a modal.
     *
     * @param {HTMLElement} target - The clicked link element.
     * @returns {boolean} True if the modal should be opened; otherwise, false.
     */
    shouldOpenModal(target) {
        const href = target.getAttribute('href');
        if (!href || !href.startsWith('#modal-')) {
            return false;
        }
        if (
            typeof MicroModal === 'undefined' ||
            !document.querySelector(href)
        ) {
            console.log(`Popup with id "${href}" doesn't exist`);
            return false;
        }

        return true;
    }

    /**
     * Registers the auto-popup to be processed after a short delay.
     * Ensures a popup is present before attempting to show it.
     */
    registerAutoPopup() {
        const popupId = this.getAutoPopupId();
        if (! popupId) return;

        // Delay the popup display by 5 second to allow the page to fully load
        setTimeout(() => this.processAutoPopup(), 5000);
    }

    /**
     * Handles the automatic popup logic:
     * - Checks if it should be shown
     * - Sets session flag
     * - Displays the popup
     */
    processAutoPopup() {
        if (! this.shouldShowAutoPopup()) return;

        this.markAutoPopupAsShown();
        this.displayAutoPopup();

        // Apply Choices JS for select elements, if available.
        const formSelects = document.querySelectorAll('#' + this.getAutoPopupId() + ' select');
        if (formSelects[0] && window.Choices) {
            formSelects.forEach((select) => {
                new Choices(select, { searchEnabled: false, shouldSort: false });
            });
        }
    }

    /**
     * Determines whether the auto-popup should be displayed.
     * It will only show if the popup exists and hasn't been shown in the session yet.
     *
     * @returns {boolean} True if the popup should be shown, false otherwise.
     */
    shouldShowAutoPopup() {
        const popupId = this.getAutoPopupId();
        if (! popupId) return false;

        const storageKey = this.getAutoPopupStorageKey(popupId);
        return !sessionStorage.getItem(storageKey);
    }

    /**
     * Marks the auto-popup as shown in the current session using sessionStorage.
     */
    markAutoPopupAsShown() {
        const popupId = this.getAutoPopupId();
        if (! popupId) return;

        const storageKey = this.getAutoPopupStorageKey(popupId);
        sessionStorage.setItem(storageKey, '1');
    }

    /**
     * Displays the auto-popup modal using MicroModal.
     */
    displayAutoPopup() {
        const popupId = this.getAutoPopupId();
        if (! popupId) return;

        MicroModal.show(popupId);
    }

    /**
     * Retrieves the ID of the first modal element set to autoload.
     *
     * @returns {string|false} The popup ID if found, otherwise false.
     */
    getAutoPopupId() {
        const modalElement = document.querySelector('.js-popup-autoload .modal');
        return modalElement ? modalElement.id : false;
    }

    /**
     * Generates a unique sessionStorage key for a given popup ID.
     *
     * @param {string} popupId - The ID of the popup.
     * @returns {string} The session storage key for tracking popup visibility.
     */
    getAutoPopupStorageKey(popupId) {
        return `auto-popup--${popupId}`;
    }
}
export default function customMicromodalCall() {
    new CustomMicroModal();
}
